import { useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Youtube, CheckCircle2, Loader2 } from 'lucide-react';
import { useSocialAccount } from '@/lib/admin/youtube-queries';
import { useStartYoutubeOAuth } from '@/lib/admin/youtube-mutations';
import { takeOAuthReturn } from '@/lib/admin/connections';

export function TutorAdminYoutubeConnectPage() {
  const { data: account, isLoading } = useSocialAccount('youtube');
  const start = useStartYoutubeOAuth();
  const navigate = useNavigate();
  // retour de Google après « Relier » dans « Paramètres de connexion » : on y retourne (et on teste)
  useEffect(() => {
    if (takeOAuthReturn()) void navigate({ to: '/tutor/admin/connexions', replace: true });
  }, [navigate]);

  async function connect() {
    const { url } = await start.mutateAsync();
    window.location.href = url;
  }

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/youtube"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        YouTube Studio
      </Link>

      <div className="border-ink-200 rounded-2xl border bg-white p-6 text-center">
        <Youtube className="text-accent-500 mx-auto h-10 w-10" />
        <h1 className="font-display text-ink-900 mt-3 text-xl font-extrabold">Connexion YouTube</h1>
        <p className="text-ink-500 mx-auto mt-2 max-w-md font-sans text-sm">
          Connecte la chaîne YouTube sur laquelle les vidéos seront publiées (OAuth Google : envoi
          des vidéos et gestion des playlists). Le jeton de rafraîchissement est chiffré côté
          serveur. Une chaîne connectée avant les playlists doit être reconnectée une fois pour que
          les animations soient rangées dans leur playlist.
        </p>

        {isLoading ? (
          <Loader2 className="text-ink-400 mx-auto mt-6 h-5 w-5 animate-spin" />
        ) : account ? (
          <div className="mt-6">
            <p className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Connectée : {account.channel_title ?? 'YouTube'}
            </p>
            <button
              type="button"
              onClick={connect}
              disabled={start.isPending}
              className="border-ink-200 text-ink-700 mt-4 inline-flex h-11 items-center justify-center gap-2 rounded-xl border bg-white px-5 text-sm font-bold transition-all active:scale-[.98] disabled:opacity-50"
            >
              Reconnecter une autre chaîne
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={connect}
            disabled={start.isPending}
            className="bg-accent-400 font-display hover:bg-accent-500 mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-xl px-6 text-sm font-bold text-white shadow-md transition-all active:scale-[.98] disabled:opacity-50"
          >
            {start.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Youtube className="h-4 w-4" />
            )}
            Connecter ma chaîne
          </button>
        )}
        {start.isError && (
          <p className="text-accent-600 mt-3 text-xs">{(start.error as Error).message}</p>
        )}
      </div>
    </div>
  );
}
